import {useDispatch, useSelector} from 'react-redux'
import React, {useEffect} from 'react'
import {Controller, useForm} from 'react-hook-form'
import {joiResolver} from '@hookform/resolvers/joi'
import _ from 'lodash'
import {
  VmoButton,
  VmoForm,
  VmoFormDropdown,
  VmoFormField,
  VmoFormInput,
  VmoModal,
  VmoModalActions,
  VmoModalContent,
  VmoModalHeader,
  VmoTooltip,
} from 'vmo-library'

import {CREATE_TAG, EDIT_TAG_SETTING} from 'actions/types'
import {getTags} from 'actions/settings'
import {tagsSchema} from 'validation/settings/productivity/tags.schema'
import {colorOptions} from 'constants/variables'
import useUserPermissions from 'hooks/pure/useUserPermission'
import {AiOutlineInfoCircle} from 'react-icons/ai'
import LockPermissionTooltip from './LockPermissionTooltip'
import {Input} from './Form'

const CreateLabel = props => {
  const {
    handleSubmit,
    formState: {errors},
    control,
    reset,
    formState,
  } = useForm({
    mode: 'onTouched',
    shouldFocusError: true,
    reValidateMode: 'onChange',
    submitFocusError: true,
    shouldUnregister: false,
    resolver: joiResolver(tagsSchema),
    defaultValues: {
      label: '',
      color: 'blue',
    },
  })

  const {isDirty} = formState

  const {isModalOpen, setIsModalOpen, createMethod, action, contactIds, conversationIds, editId} = props

  const dispatch = useDispatch()

  const {type, isLoadingData, successLabels = []} = useSelector(state => state.apiReducer)
  const {getTagData} = useSelector(state => state.settings)

  const {workPermissions} = useUserPermissions()
  const allowManageTagsRole = workPermissions?.manage_tags

  useEffect(() => {
    if (successLabels.includes(CREATE_TAG) || successLabels.includes(EDIT_TAG_SETTING)) {
      setIsModalOpen(false)
      dispatch(getTags())
    }
  }, [successLabels, dispatch, setIsModalOpen])

  useEffect(() => {
    if (editId) {
      reset(_.pick(getTagData, ['label', 'color']))
    }
  }, [editId, getTagData, reset])

  const handleSubmittedData = data => {
    if (action) return dispatch(action(data, editId))

    if (contactIds) {
      data.contactIds = contactIds
    }
    if (conversationIds) {
      data.conversationIds = conversationIds
    }
    if (createMethod) return createMethod(data)
  }

  return (
    <VmoModal closeIcon size="mini" centered={false} open={isModalOpen} onClose={() => setIsModalOpen(false)}>
      <VmoModalHeader>{action ? 'Edit' : 'Create'} Tag</VmoModalHeader>
      <VmoModalContent>
        <VmoForm className="errorLabel">
          <Input
            name="label"
            control={control}
            label="Tag Name"
            placeholder="Write tag name.."
            tooltipProps={{content: 'Specify name of the tag'}}
            maxLength={20}
            required
          />
          <VmoFormField>
            <div className="info-header">
              <label>Choose Color</label>
              <VmoTooltip content="Set tag color for unique identity" trigger={<AiOutlineInfoCircle />} />
            </div>

            <Controller
              control={control}
              name="color"
              render={({field: {value, onChange}}) => (
                <VmoFormDropdown
                  placeholder="select color"
                  selection
                  options={colorOptions}
                  onChange={(e, {value}) => onChange(value)}
                  value={value}
                />
              )}
            />
          </VmoFormField>
        </VmoForm>
      </VmoModalContent>
      <VmoModalActions>
        <VmoButton disabled={isLoadingData && type.includes(CREATE_TAG)} basic onClick={() => setIsModalOpen(false)}>
          Close
        </VmoButton>
        <LockPermissionTooltip isRoleAccessDenied={!allowManageTagsRole}>
          <VmoButton
            primary
            className={!allowManageTagsRole ? 'disabled-button' : ''}
            loading={isLoadingData && type.includes(CREATE_TAG)}
            disabled={!isDirty || (isLoadingData && type.includes(CREATE_TAG))}
            {...(allowManageTagsRole && {
              onClick: handleSubmit(data => handleSubmittedData(data)),
            })}
          >
            {action ? 'Update' : 'Create'} Tag
          </VmoButton>
        </LockPermissionTooltip>
      </VmoModalActions>
    </VmoModal>
  )
}

export default CreateLabel
